import { useEffect, useState, useContext } from 'react';
import { AuthContext } from '../../contexts/auth';
import { SimulationContext } from '../../contexts/simulation';
import { useTranslation } from 'react-i18next';
import { AppLayout } from '../../components/AppLayout';
import { CardIcon } from '../../components/CardIcon';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Progress } from '../../components/ui/progress';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Plus,
  Target,
  TrendingUp,
  Calendar,
  DollarSign,
  CheckCircle2,
  Calculator,
  Trash2,
  Pencil
} from 'lucide-react';
import { toast } from 'sonner';
import {
  createSimulationContribution,
  listSimulationContributions,
  updateSimulationContribution,
  deleteSimulationContribution
} from '../../api/simulationContributions';
import { intlLocale } from '../../Utils/locale';
import { useCurrency } from '../../contexts/Currency';

const CATEGORY_LABELS = {
  'Viagem': { pt: 'Viagem', en: 'Travel' },
  'Aposentadoria': { pt: 'Aposentadoria', en: 'Retirement' },
  'Imóvel': { pt: 'Imóvel', en: 'Real Estate' },
  'Veículo': { pt: 'Veículo', en: 'Vehicle' },
  'Educação': { pt: 'Educação', en: 'Education' },
  'Emergência': { pt: 'Fundo de Emergência', en: 'Emergency Fund' },
  'Outros': { pt: 'Outros', en: 'Others' },
};

function parseCurrencyWithParts(value, parts) {
  if (value == null || value === '') return 0;
  if (typeof value === 'number') return value;
  let s = String(value);
  if (parts?.symbol) s = s.split(parts.symbol).join('');
  s = s.replace(/\s/g, '');
  if (parts?.thousandSeparator) {
    s = s.split(parts.thousandSeparator).join('');
  }
  if (parts?.decimalSeparator && parts.decimalSeparator !== '.') {
    s = s.replace(parts.decimalSeparator, '.');
  }
  s = s.replace(/[^\d.-]/g, '');
  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
}

function Simulation() {
  const { user } = useContext(AuthContext);
  const { saveSimulation, simulation, getSimulations, excluirSimulacao, updateSimulacaoValues} = useContext(SimulationContext)
  const { t, i18n } = useTranslation();
  const language = i18n.language;
  const { format: formatCurrency, parts: currencyParts } = useCurrency();
  const parseCurrencyToNumber = (value) => parseCurrencyWithParts(value, currencyParts);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [metodoCalculo, setMetodoCalculo] = useState('meta-prazo');

  // Form states
  const [objetivo, setObjetivo] = useState('');
  const [categoria, setCategoria] = useState('Viagem');
  const [valorinicial, setValorinicial] = useState('');
  const [aportemensal, setAportemensal] = useState('');
  const [tempoinvestido, setTempoinvestido] = useState('');
  const [rendimentomensal, setRendimentomensal] = useState('0.8');

  // Checkboxes do tipo de objetivo
  const [showAporteMensal, setShowAporteMensal] = useState(false);
  const [showCalculadora, setShowCalculadora] = useState(false);

  const handleToggleAporteMensal = (checked) => {
  setShowAporteMensal(checked);

  // desmarcou → limpa aporte
  if (!checked) {
    setAportemensal('');
    setMetaValor('');
  }

  // se nenhum marcado → objetivo simples → limpa tudo financeiro
  if (!checked && !showCalculadora) {
    setValorinicial('');
    setAportemensal('');
    setRendimentomensal('0.8');
    setMetaValor('');
    // setTempoinvestido(''); // só se vc quiser zerar o prazo também
  }
};

const handleToggleCalculadora = (checked) => {
  setShowCalculadora(checked);

  // desmarcou → limpa campos da calculadora
  if (!checked) {
    setValorinicial('');
    setRendimentomensal('0.8');
    // setMetodoCalculo('meta-prazo'); // se quiser resetar também
  }

  // se nenhum marcado → objetivo simples → limpa tudo financeiro
  if (!checked && !showAporteMensal) {
    setValorinicial('');
    setAportemensal('');
    setRendimentomensal('0.8');
    // setTempoinvestido(''); // só se vc quiser zerar o prazo também
  }
};

const [metaDescricao, setMetaDescricao] = useState('');
const [metaValor, setMetaValor] = useState('');

  const [contributionsBySimulation, setContributionsBySimulation] = useState({});
  const [expandedContributions, setExpandedContributions] = useState({});
  const [contributionFormBySimulation, setContributionFormBySimulation] = useState({});
  const [loadingContributionsBySimulation, setLoadingContributionsBySimulation] = useState({});
  const [savingContributionBySimulation, setSavingContributionBySimulation] = useState({});
  const [editingContributionBySimulation, setEditingContributionBySimulation] = useState({});
  const [deletingContributionById, setDeletingContributionById] = useState({});

  const setContributionForm = (simulationId, patch) => {
    setContributionFormBySimulation((prev) => {
      const base = prev[simulationId] || { valor: '', data: '', descricao: '' };
      return {
        ...prev,
        [simulationId]: { ...base, ...patch },
      };
    });
  };

  const fetchContributions = async (simulationId) => {
    setLoadingContributionsBySimulation((prev) => ({
      ...prev,
      [simulationId]: true,
    }));

    try {
      const list = await listSimulationContributions(simulationId);
      setContributionsBySimulation((prev) => ({
        ...prev,
        [simulationId]: Array.isArray(list) ? list : [],
      }));
    } catch (err) {
      console.error('Erro ao buscar aportes:', err?.response?.data || err?.message);
      toast.error(t('errorLoadingContributions'));
      setContributionsBySimulation((prev) => ({
        ...prev,
        [simulationId]: [],
      }));
    } finally {
      setLoadingContributionsBySimulation((prev) => ({
        ...prev,
        [simulationId]: false,
      }));
    }
  };

  const handleCreateContribution = async (simulationId) => {
    const form = contributionFormBySimulation[simulationId] || { valor: '', data: '', descricao: '' };
    const valorNumber = parseCurrencyToNumber(form.valor);

    if ((!valorNumber && valorNumber !== 0) || isNaN(valorNumber) || valorNumber <= 0) {
      toast.error(t('enterValidContributionAmount'));
      return;
    }

    if (!form.data) {
     toast.error(t('selectContributionDate'));
      return;
    }

    if (!form.descricao?.trim()) {
      toast.error(t('enterContributionDescription'));
      return;
    }

    setSavingContributionBySimulation((prev) => ({
      ...prev,
      [simulationId]: true,
    }));

    const editingId = editingContributionBySimulation[simulationId];

    try {
      if (editingId) {
        await updateSimulationContribution(editingId, {
          valor: valorNumber,
          data: form.data,
          descricao: form.descricao,
        });
        toast.success(t('contributionUpdated'));
        setEditingContributionBySimulation((prev) => {
          const next = { ...prev };
          delete next[simulationId];
          return next;
        });
      } else {
        await createSimulationContribution({
          simulationId,
          valor: valorNumber,
          data: form.data,
          descricao: form.descricao,
        });
        toast.success(t('contributionAdded'));
      }

      setContributionFormBySimulation((prev) => ({
        ...prev,
        [simulationId]: { valor: '', data: '', descricao: '' },
      }));

      await fetchContributions(simulationId);
      if (user?.id) {
        await getSimulations(user.id);
      }
    } catch (err) {
      console.error('Erro ao registrar aporte:', err?.response?.data || err?.message);
      toast.error(t('errorAddingContribution'));
    } finally {
      setSavingContributionBySimulation((prev) => ({
        ...prev,
        [simulationId]: false,
      }));
    }
  };

  const handleEditContribution = (contribution, simulationId) => {
    const valor = contribution.valor ?? contribution.valorAportado ?? contribution.amount ?? 0;
    const data = contribution.data || contribution.date || '';
    const dataFormatted = data ? new Date(data).toISOString().slice(0, 10) : '';

    setContributionFormBySimulation((prev) => ({
      ...prev,
      [simulationId]: {
        valor: formatCurrency(Number(valor)),
        data: dataFormatted,
        descricao: contribution.descricao || contribution.description || '',
      },
    }));
    setEditingContributionBySimulation((prev) => ({
      ...prev,
      [simulationId]: contribution._id || contribution.id,
    }));
  };

  const handleCancelEditContribution = (simulationId) => {
    setContributionFormBySimulation((prev) => ({
      ...prev,
      [simulationId]: { valor: '', data: '', descricao: '' },
    }));
    setEditingContributionBySimulation((prev) => {
      const next = { ...prev };
      delete next[simulationId];
      return next;
    });
  };

  const handleDeleteContribution = async (contributionId, simulationId) => {
    const confirme = window.confirm(t('confirmRemoveContribution'));

    if (!confirme) return;

    setDeletingContributionById((prev) => ({
      ...prev,
      [contributionId]: true,
    }));

    try {
      await deleteSimulationContribution(contributionId);
      toast.success(t('contributionRemoved'));

      await fetchContributions(simulationId);
      if (user?.id) {
        await getSimulations(user.id);
      }
    } catch (err) {
      console.error('Erro ao remover aporte:', err?.response?.data || err?.message);
      toast.error(t('errorRemovingContribution'));
    } finally {
      setDeletingContributionById((prev) => ({
        ...prev,
        [contributionId]: false,
      }));
    }
  };


useEffect(() => {
    if (user?.id) {
      getSimulations(user.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  useEffect(() => {
    if (simulation && simulation.length > 0) {
      simulation.forEach((sim) => {
        if (sim._id && !contributionsBySimulation[sim._id]) {
          fetchContributions(sim._id);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simulation]);

  const calcularSimulacao = () => {
    const capital = parseCurrencyToNumber(valorinicial);
    const aporte = parseCurrencyToNumber(aportemensal);
    const meses = parseInt(tempoinvestido) || 1;
    const taxa = parseFloat(rendimentomensal) / 100;

    // Guarda contra divisão por zero quando rendimento = 0%
    const montante = (Number.isFinite(taxa) && taxa !== 0)
      ? capital * Math.pow(1 + taxa, meses) + aporte * (((Math.pow(1 + taxa, meses) - 1) / taxa))
      : capital + (aporte * meses);

    const saldofinal = montante;
    const retornomensal = saldofinal * (Number.isFinite(taxa) ? taxa : 0);
    const retornoanual = retornomensal * 12;
    const totalmeses = meses;

    return {
      saldofinal: parseFloat(saldofinal.toFixed(2)),
      retornomensal: parseFloat(retornomensal.toFixed(2)),
      retornoanual: parseFloat(retornoanual.toFixed(2)),
      totalmeses
    };
  };

 const handleSubmit = () => {
  // ✅ obrigatórios sempre (objetivo simples também)
  if (!objetivo || !categoria || !metaDescricao || !tempoinvestido) {
    toast.error(t('fillRequiredFields'));
    return;
  }

  // ✅ Aporte simples (sem calculadora)
  if (showAporteMensal && !showCalculadora) {
    if (!aportemensal) {
      toast.error(t('enterMonthlyContribution'));
      return;
    }
    if (!metaValor) {
      toast.error(t('enterGoalAmount'));
      return;
    }
  }

  // ✅ Calculadora
  if (showCalculadora) {
    if (!valorinicial || !rendimentomensal || !aportemensal) {
      toast.error(t('fillCalculatorFields'));
      return;
    }
  }

  const prazo = parseInt(tempoinvestido);
  const resultado = calcularSimulacao();

  const categoriaFinal = categoria

    const data = {
      userId: user.id,
      objetivo,
      categoria: categoriaFinal,
      metaDescricao,
      metaValor: parseCurrencyToNumber(metaValor),
      showAporteMensal,
      showCalculadora,
      valorinicial: parseCurrencyToNumber(valorinicial),
      aportemensal: parseCurrencyToNumber(aportemensal),
      tempoinvestido: prazo,
      rendimentomensal: parseFloat(rendimentomensal),
      saldofinal: resultado.saldofinal,
      retornomensal: resultado.retornomensal,
      retornoanual: resultado.retornoanual,
      totalmeses: resultado.totalmeses
    };


    saveSimulation(data);
    toast.success(t('goalCreatedSuccessfully'));
    closeDialog();
  };

const resetCreateForm = () => {
  // ✅ reset campos base
  setObjetivo('');
  setCategoria('Viagem');
  setMetaDescricao('');   // ✅ AQUI (o que faltava)
  setMetaValor('');
  setTempoinvestido('');

  // ✅ reset financeiros
  setValorinicial('');
  setAportemensal('');
  setRendimentomensal('0.8');
  setMetodoCalculo('meta-prazo');

  // ✅ reset checkboxes
  setShowAporteMensal(false);
  setShowCalculadora(false);
};

const closeDialog = () => {
  setIsDialogOpen(false);
  resetCreateForm();
};

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editObj, setEditObj] = useState(null);
  const [editMetodoCalculo, setEditMetodoCalculo] = useState("meta-prazo");

  const handleEdit = (item) => {
  setEditObj(item);

  // ✅ checkbox deve refletir o objetivo salvo
  setShowAporteMensal(!!item?.aportemensal || !!item?.showAporteMensal);
  setShowCalculadora(!!item?.valorinicial || !!item?.showCalculadora);

  setIsEditOpen(true);
};


  const handleDelete = (id) => {
    const confirme = window.confirm(t('confirmDeleteGoal'));
    if (confirme) {
      excluirSimulacao(id, user.id);
      toast.success(t('goalDeleted')); 
    }
  };
  const setMoneyOnEditObj = (field, rawValue) => {
  const numeric = String(rawValue || "").replace(/\D/g, "");
  const value = Number(numeric) / 100;

  setEditObj((prev) => ({
    ...prev,
    [field]: value,
  }));
};

  const calcularProgresso = (obj, totalAportadoOverride) => {
    const isFinanceiro = !!obj?.showAporteMensal || !!obj?.showCalculadora;

    const toNumber = (value) => {
      if (typeof value === 'string') {
        return parseCurrencyToNumber(value);
      }
      return Number(value) || 0;
    };

    if (isFinanceiro) {
      const totalAportado = Number.isFinite(totalAportadoOverride)
        ? totalAportadoOverride
        : toNumber(obj?.totalAportado);
      const metaValor = toNumber(obj?.metaValor) || toNumber(obj?.saldofinal);

      if (metaValor > 0) {
        const percent = (totalAportado / metaValor) * 100;
        return Math.max(0, Math.min(100, percent));
      }

      const percent = Number(obj?.percentualAtingido);
      if (Number.isFinite(percent)) {
        return Math.max(0, Math.min(100, percent));
      }
    }

    const valorInicial = toNumber(obj?.valorinicial);
    const meta = toNumber(obj?.saldofinal);
    if (meta > 0) {
      return (valorInicial / meta) * 100;
    }
    return 0;
  };

const locale = intlLocale(language);

const getDataConclusao = (meses) => {
  const data = new Date();

  data.setMonth(data.getMonth() + meses);

  return data.toLocaleDateString(locale, {
    month: 'long',
    year: 'numeric'
  });
};

  return (
    <AppLayout
      title={t('goals')}
      subtitle={t('setYourGoalsAndTrackProgress')}
      icon={Target}
      headerActions={
             <Dialog
              open={isDialogOpen}
              onOpenChange={(open) => {
                if (!open) closeDialog();
                else {
                  resetCreateForm();
                  setIsDialogOpen(true);
                }
              }}
            >
            <DialogTrigger asChild>
              <Button
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                onClick={() => {
                  resetCreateForm();
                  setIsDialogOpen(true);
                }}
              >
                <Plus className="w-4 h-4 mr-2" />
                {t('newGoal')}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>
                        {t('createNewGoal')}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                   <Label>{t('goalName')}</Label>
                  <Input
                    placeholder={t('goalNameExample')}
                    value={objetivo}
                    onChange={(e) => setObjetivo(e.target.value)}
                  />
                </div>

                  <div className="space-y-2">
                   <Label>{t('category')}</Label>
                  <Select value={categoria} onValueChange={setCategoria}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(CATEGORY_LABELS).map(([key, labels]) => (
                        <SelectItem key={key} value={key}>{labels[language] || key}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                 {/* ✅ CHECKBOXES  */}
                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showAporteMensal}
                      onChange={(e) => handleToggleAporteMensal(e.target.checked)}
                    />
                  <span>{t('monthlyContribution')}</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={showCalculadora}
                      onChange={(e) => handleToggleCalculadora(e.target.checked)}
                    />
                    <span>{t('yieldCalculator')}</span>
                  </label>
                </div>

                
{/* ✅ Meta / Descrição do objetivo (sempre) */}
<div className="space-y-2">
  <Label>
   {t('goalDescription')}
  </Label>
  <Input
    placeholder={t('goalDescriptionExample')}
    value={metaDescricao}
    onChange={(e) => setMetaDescricao(e.target.value)}
  />
</div>

{/* ✅ Meta em valor (somente quando marcar Aporte mensal) */}
{showAporteMensal && (
  <div className="space-y-2">
    <Label>
     {t('financialGoal')}
    </Label>
    <Input
      type="text"
      inputMode="numeric"
      placeholder={t('currencyPlaceholder')}
      value={metaValor}
      onChange={(e) => {
        const numeric = e.target.value.replace(/\D/g, '');
        const formatted = formatCurrency(Number(numeric) / 100);
        setMetaValor(formatted);
      }}
    />
  </div>
)}

{/* ✅ Prazo simples (somente quando NÃO estiver usando calculadora) */}
{!showCalculadora && (
  <div className="space-y-2">
  <Label>{t('termMonths')}</Label>
    <Input
      type="number"
      placeholder="12"
      value={tempoinvestido}
      onChange={(e) => setTempoinvestido(e.target.value)}
    />
  </div>
)}

{/* ✅ Aporte simples (quando marcar Aporte e NÃO marcar Calculadora) */}
{showAporteMensal && !showCalculadora && (
  <div className="space-y-2">
    <Label>{t('monthlyContribution')}</Label>
    <Input
      type="text"
      inputMode="numeric"
      placeholder={t('currencyPlaceholder')}
      value={aportemensal}
      onChange={(e) => {
        const numeric = e.target.value.replace(/\D/g, '');
        const formatted = formatCurrency(Number(numeric) / 100);
        setAportemensal(formatted);
      }}
    />
  </div>
)}

{/* ✅ BLOCO COMPLETO DA CALCULADORA (quando marcada) */}
{showCalculadora && (
  <>
    {/* Valores principais */}
    <div className="grid grid-cols-2 gap-4">
      <div className="space-y-2">
        <Label>{t('initialValue')}</Label>
        <Input
          type="text"
          inputMode="numeric"
          placeholder={t('currencyPlaceholder')}
          value={valorinicial}
          onChange={(e) => {
            const numeric = e.target.value.replace(/\D/g, '');
            const formatted = formatCurrency(Number(numeric) / 100);
            setValorinicial(formatted);
          }}
        />
      </div>

      <div className="space-y-2">
        <Label>{t('monthlyReturn')}{' '}</Label>
        <Input
          type="number"
          step="0.1"
          placeholder="0.8"
          value={rendimentomensal}
          onChange={(e) => setRendimentomensal(e.target.value)}
        />
      </div>
    </div>

    {/* Método de cálculo */}
    <div className="space-y-3">
      <Label className="flex items-center gap-2">
        <Calculator className="w-4 h-4" />
       {t('calculationMethod')}
      </Label>

      <Tabs value={metodoCalculo} onValueChange={setMetodoCalculo}>
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="meta-prazo">
           {t('setGoalAndTerm')}
          </TabsTrigger>
          <TabsTrigger value="aporte-fixo">
           {t('setFixedContribution')}
          </TabsTrigger>
        </TabsList>

        {/* TAB 1: Meta + prazo */}
        <TabsContent value="meta-prazo" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>{t('termMonths')}</Label>
            <Input
              type="number"
              placeholder="12"
              value={tempoinvestido}
              onChange={(e) => setTempoinvestido(e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('monthlyContribution')}</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder={t('currencyPlaceholder')}
              value={aportemensal}
              onChange={(e) => {
                const numeric = e.target.value.replace(/\D/g, '');
                const formatted = formatCurrency(Number(numeric) / 100);
                setAportemensal(formatted);
              }}
            />
          </div>

          {valorinicial && tempoinvestido && aportemensal && (
            <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-4 h-4 text-blue-600" />
                <p className="text-sm text-slate-700 font-medium">
                 {t('simulationResult')}
                </p>
              </div>

              <p className="text-slate-900">
               {t('finalBalance')}{' '}
                <span className="font-semibold">
                  {formatCurrency(calcularSimulacao().saldofinal)}
                </span>
              </p>

              <p className="text-slate-700 text-sm mt-1">
              {t('monthlyReturn')}{' '}
                {formatCurrency(calcularSimulacao().retornomensal)}
              </p>
            </Card>
          )}
        </TabsContent>

        {/* TAB 2: Aporte fixo */}
        <TabsContent value="aporte-fixo" className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label>{t('monthlyContribution')}</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder={t('currencyPlaceholder')}
              value={aportemensal}
              onChange={(e) => {
                const numeric = e.target.value.replace(/\D/g, '');
                const formatted = formatCurrency(Number(numeric) / 100);
                setAportemensal(formatted);
              }}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('termMonths')}</Label>
            <Input
              type="number"
              placeholder="12"
              value={tempoinvestido}
              onChange={(e) => setTempoinvestido(e.target.value)}
            />
          </div>

          {valorinicial && aportemensal && tempoinvestido && (
            <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 border-green-200">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-4 h-4 text-green-600" />
                <p className="text-sm text-slate-700 font-medium">
                  {t('simulationResult')}
                </p>
              </div>

              <p className="text-slate-900">
                {t('finalBalance')}{' '}
                <span className="font-semibold">
                  {formatCurrency(calcularSimulacao().saldofinal)}
                </span>
              </p>

              <p className="text-slate-600 text-sm mt-1">
              {t('expectedCompletion')}{' '}
                {getDataConclusao(parseInt(tempoinvestido))}
              </p>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  </>
)}


                <Button
                  className="w-full"
                  onClick={handleSubmit}
                  disabled={
                    !objetivo ||
                    !categoria ||
                      // ✅ Objetivo simples (nenhum checkbox marcado)
                    (!showAporteMensal && !showCalculadora && (!metaDescricao || !tempoinvestido)) ||

                    // ✅ Só aporte mensal (sem calculadora)
                    (showAporteMensal && !showCalculadora && (!aportemensal || !tempoinvestido)) ||

                    // ✅ Calculadora ativa
                    (showCalculadora && (!valorinicial || !rendimentomensal || !aportemensal || !tempoinvestido))
                  }
                >
                  {t('createNewGoal')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
      }
    >

        <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
        <DialogContent className="max-w-2xl">
       <DialogHeader>
      <DialogTitle>
       {t('editGoal')}
      </DialogTitle>
    </DialogHeader>

    {editObj && (
      <div className="space-y-4">

           
        {/* Nome */}
        <div className="space-y-2">
          <Label>{t('goalName')}</Label>
          <Input
            placeholder={t('goalNameExample')}
            value={editObj.objetivo ?? ""}
            onChange={(e) =>
              setEditObj((prev) => ({ ...prev, objetivo: e.target.value }))
            }
          />
        </div>

        {/* Categoria */}
        <div className="space-y-2">
          <Label>{t('category')}</Label>
          <Select
            value={editObj.categoria ?? ""}
            onValueChange={(value) =>
              setEditObj((prev) => ({ ...prev, categoria: value }))
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.entries(CATEGORY_LABELS).map(([key, labels]) => (
                <SelectItem key={key} value={key}>{labels[language] || key}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

         {/* ✅ CHECKBOXES  */}
            <div className="flex items-center gap-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showAporteMensal}
                  onChange={(e) => handleToggleAporteMensal(e.target.checked)}
                />
                <span>{t('monthlyContribution')}</span>
              </label>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={showCalculadora}
                  onChange={(e) => handleToggleCalculadora(e.target.checked)}
                />
             <span>{t('yieldCalculator')}</span>
              </label>
            </div>

            {/* ✅ Meta / Descrição (somente objetivo simples) */}
              {(!showAporteMensal && !showCalculadora) && (
                <div className="space-y-2">
                  <Label> {t('goalDescription')}</Label>
                  <Input
                    placeholder={t('goalNameExample')}
                    value={editObj?.metaDescricao ?? ''}
                    onChange={(e) =>
                      setEditObj((prev) => ({ ...prev, metaDescricao: e.target.value }))
                    }
                  />
                </div>
              )}

            {/* ✅ Meta financeira (quando Aporte mensal estiver marcado) */}
            {showAporteMensal && (
              <div className="space-y-2">
                <Label>{t('financialGoal')}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder={t('currencyPlaceholder')}
                  value={
                    editObj?.metaValor
                      ? formatCurrency(editObj.metaValor)
                      : ''
                  }
                  onChange={(e) => setMoneyOnEditObj("metaValor", e.target.value)}
                />
              </div>
            )}


            {/* ✅ Prazo simples (somente quando NÃO estiver usando calculadora) */}
              {!showCalculadora && (
                <div className="space-y-2">
                  <Label>{t('termMonths')}</Label>
                  <Input
                    type="number"
                    placeholder="12"
                    value={editObj.tempoinvestido ?? ""}
                    onChange={(e) =>
                      setEditObj((prev) => ({
                        ...prev,
                        tempoinvestido: Number(e.target.value),
                      }))
                    }
                  />
                </div>
              )}
              {/* ✅ Aporte simples (fica AQUI: entre prazo simples e calculadora) */}
            {showAporteMensal && !showCalculadora && (
              <div className="space-y-2">
                <Label>{t('monthlyContribution')}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder={t('currencyPlaceholder')}
                  value={formatCurrency(editObj.aportemensal ?? 0)}
                  onChange={(e) => setMoneyOnEditObj("aportemensal", e.target.value)}
                />
              </div>
            )}

              {showCalculadora && (
  <>
        {/* Grid 2 colunas (igual ao Criar) */}
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>{t('initialValue')}</Label>
            <Input
              type="text"
              inputMode="numeric"
              placeholder={t('currencyPlaceholder')}
              value={
                      editObj?.valorinicial
                        ? formatCurrency(editObj.valorinicial)
                        : ''
                    }
              onChange={(e) => setMoneyOnEditObj("valorinicial", e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label>{t('monthlyReturn')}{' '}</Label>
            <Input
              type="number"
              step="0.1"
              placeholder="0.8"
              value={editObj.rendimentomensal ?? 0}
              onChange={(e) =>
                setEditObj((prev) => ({
                  ...prev,
                  rendimentomensal: Number(e.target.value),
                }))
              }
            />
          </div>
        </div>
        
        {/* Método de Cálculo (igual ao Criar, só layout) */}
        <div className="space-y-3">
          <Label className="flex items-center gap-2">
            <Calculator className="w-4 h-4" />
            {t('calculationMethod')}
          </Label>

          <Tabs value={editMetodoCalculo} onValueChange={setEditMetodoCalculo}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="meta-prazo">
                {t('setGoalAndTerm')}
              </TabsTrigger>
              <TabsTrigger value="aporte-fixo">
                  {t('setFixedContribution')}
              </TabsTrigger>
            </TabsList>

            {/* META PRAZO - igual */}
            <TabsContent value="meta-prazo" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('termMonths')}</Label>
                <Input
                  type="number"
                  placeholder="12"
                  value={editObj.tempoinvestido ?? ""}
                  onChange={(e) =>
                    setEditObj((prev) => ({
                      ...prev,
                      tempoinvestido: Number(e.target.value),
                    }))
                  }
                />
              </div>

              <div className="space-y-2">
                <Label><Label>{t('monthlyContribution')}</Label></Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder={t('currencyPlaceholder')}
                  value={
                        editObj?.aportemensal
                          ? formatCurrency(editObj.aportemensal)
                          : ''
                      }
                  onChange={(e) => setMoneyOnEditObj("aportemensal", e.target.value)}
                />
              </div>
            </TabsContent>

            {/* APORTE FIXO - layout igual */}
            <TabsContent value="aporte-fixo" className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>{t('monthlyContribution')}</Label>
                <Input
                  type="text"
                  inputMode="numeric"
                  placeholder={t('currencyPlaceholder')}
                  value={formatCurrency(editObj.aportemensal ?? 0)}
                  onChange={(e) => setMoneyOnEditObj("aportemensal", e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>{t('termMonths')}</Label>
                <Input
                  type="number"
                  placeholder="12"
                  value={editObj.tempoinvestido ?? ""}
                  onChange={(e) =>
                    setEditObj((prev) => ({
                      ...prev,
                      tempoinvestido: Number(e.target.value),
                    }))
                  }
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
         </>
)}

        {/* Botões */}
        <div className="flex gap-2 justify-end">
          <Button variant="outline" onClick={() => setIsEditOpen(false)}>
             {t('cancel')}
          </Button>

          <Button
            onClick={async () => {
              // ✅ Recalcular antes de salvar
              const capital = Number(editObj.valorinicial) || 0;
              const aporte = Number(editObj.aportemensal) || 0;
              const meses = Number(editObj.tempoinvestido) || 1;
              const taxa = (Number(editObj.rendimentomensal) || 0) / 100;

              const montante =
                capital * Math.pow(1 + taxa, meses) +
                (taxa === 0 ? aporte * meses : aporte * ((Math.pow(1 + taxa, meses) - 1) / taxa));

              const saldofinal = montante;
              const retornomensal = saldofinal * taxa;
              const retornoanual = retornomensal * 12;

              const payloadFinal = {
                    ...editObj,

                    // flags para persistir o tipo do objetivo
                    showAporteMensal,
                    showCalculadora,
                  };

                  // ✅ Objetivo simples
                  if (!showAporteMensal && !showCalculadora) {
                    payloadFinal.valorinicial = null;
                    payloadFinal.aportemensal = null;
                    payloadFinal.rendimentomensal = null;
                    payloadFinal.metodoCalculo = null;

                    payloadFinal.saldofinal = null;
                    payloadFinal.retornomensal = null;
                    payloadFinal.retornoanual = null;
                    payloadFinal.totalmeses = editObj?.tempoinvestido ?? tempoinvestido ?? null;

                  }

                  // ✅ Aporte simples (sem calculadora)
                  if (showAporteMensal && !showCalculadora) {
                    payloadFinal.aportemensal = editObj?.aportemensal || aportemensal;
                    payloadFinal.valorinicial = null;
                    payloadFinal.rendimentomensal = null;
                    payloadFinal.metodoCalculo = null;

                    payloadFinal.saldofinal = null;
                    payloadFinal.retornomensal = null;
                    payloadFinal.retornoanual = null;
                    payloadFinal.totalmeses = editObj?.tempoinvestido || null;
                  }

                  // ✅ Calculadora
                  if (showCalculadora) {
                    payloadFinal.valorinicial = editObj?.valorinicial || valorinicial;
                    payloadFinal.aportemensal = editObj?.aportemensal || aportemensal;
                    payloadFinal.rendimentomensal = editObj?.rendimentomensal || rendimentomensal;
                    payloadFinal.metodoCalculo = editMetodoCalculo;

                    payloadFinal.saldofinal = saldofinal;
                    payloadFinal.retornomensal = retornomensal;
                    payloadFinal.retornoanual = retornoanual;
                    payloadFinal.totalmeses = meses;
                  }
             await updateSimulacaoValues(payloadFinal);
             setIsEditOpen(false);
            }}
          >
            {t('save')}
          </Button>
        </div>
      </div>
    )}
  </DialogContent>
</Dialog>
          {/* Objetivos Grid */}
          <div className="grid grid-cols-1 gap-6">
            {simulation.length === 0 ? (
              <Card className="p-12 text-center">
                <Target className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-slate-900 font-semibold text-xl mb-2">
                 {t('noGoalsRegistered')}
                </h3>
                <p className="text-slate-600 mb-6">
                {t('startBySettingGoals')}
                </p>
                <Button onClick={() => setIsDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                 {t('createFirstGoal')}
                </Button>
              </Card>
            ) : (
              simulation.map((obj) => {
                const isObjetivoSimplesItem = !obj?.showAporteMensal && !obj?.showCalculadora;
                const aportes = contributionsBySimulation[obj._id] || [];
                const totalAportadoFromAportes = aportes.reduce((sum, item) => {
                  const valorAporte = item.valor ?? item.valorAportado ?? item.amount ?? 0;
                  const valorAporteNumber = typeof valorAporte === 'string'
                    ? parseCurrencyToNumber(valorAporte)
                    : Number(valorAporte) || 0;
                  return sum + valorAporteNumber;
                }, 0);
                const totalAportado = Number.isFinite(totalAportadoFromAportes)
                  ? totalAportadoFromAportes
                  : Number(obj?.totalAportado ?? 0);
                const metaValorNumber = obj?.metaValor != null
                  ? (typeof obj.metaValor === 'string' ? parseCurrencyToNumber(obj.metaValor) : Number(obj.metaValor) || 0)
                  : (typeof obj?.saldofinal === 'string' ? parseCurrencyToNumber(obj.saldofinal) : Number(obj?.saldofinal) || 0);
                const progresso = calcularProgresso(obj, totalAportado);
                const faltaParaMeta = Number.isFinite(metaValorNumber)
                  ? metaValorNumber - totalAportado
                  : 0;
                const aporteForm = contributionFormBySimulation[obj._id] || { valor: '', data: '', descricao: '' };
                const isLoadingAportes = loadingContributionsBySimulation[obj._id];
                const isSavingAporte = savingContributionBySimulation[obj._id];

                return (
                  <Card key={obj._id} className="p-6 hover:shadow-lg transition-shadow">
                    <div className="space-y-4">
                      {/* Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <CardIcon icon={Target} className="bg-blue-100" iconClassName="text-blue-600" />
                          <div>
                            <h3 className="text-slate-900 font-semibold text-lg">{obj.objetivo}</h3>
                            <Badge variant="outline" className="mt-1">
                              {CATEGORY_LABELS[obj.categoria]?.[language] || obj.categoria}
                            </Badge>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {progresso >= 100 && (
                            <CheckCircle2 className="w-8 h-8 text-green-500" />
                          )}

                          <div className="flex items-center gap-2 relative z-10">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {                          
                            handleEdit(obj);
                            }}
                            className="relative z-10 pointer-events-auto text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleDelete(obj._id)}
                            className="relative z-10 pointer-events-auto text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          </div>
                        </div>
                      </div>

                      {/* Progress */}
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-slate-600">
                        {t('progress')}
                          </span>
                          <span className="text-slate-900 font-semibold">{progresso.toFixed(1)}%</span>
                        </div>
                        <Progress value={progresso} className="h-3" />
                      </div>

                      {/* ✅ Conteúdo condicional conforme tipo do objetivo */}
                      {isObjetivoSimplesItem ? (
                        <>
                          {/* ✅ Objetivo simples: Meta/Descrição + Prazo */}
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4 border-t">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Target className="w-4 h-4 text-slate-400" />
                                <p className="text-slate-600 text-sm">
                                 {t('goalDescription')}
                                </p>
                              </div>
                              <p className="text-slate-900 font-semibold">
                                {obj?.metaDescricao || '-'}
                              </p>
                            </div>

                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                <p className="text-slate-600 text-sm">
                                {t('term')}
                                </p>
                              </div>
                              <p className="text-slate-900 font-semibold">
                                {obj?.tempoinvestido ?? obj?.totalmeses ?? '-'} {t('months')}
                              </p>
                            </div>
                          </div>
                        </>
                      ) : (
                        <>
                          {/* ? Objetivo financeiro: resumo com indicadores e aportes */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 pt-4 border-t">
                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Target className="w-4 h-4 text-slate-400" />
                                <p className="text-slate-600 text-sm">
                                  {t('financialGoal')}
                                </p>
                              </div>
                              <p className="text-slate-900 font-semibold">
                                {metaValorNumber > 0 ? formatCurrency(metaValorNumber) : '-'}
                              </p>
                            </div>

                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <DollarSign className="w-4 h-4 text-slate-400" />
                                <p className="text-slate-600 text-sm">
                              {t('totalContributed')}
                                </p>
                              </div>
                              <p className="text-slate-900 font-semibold">
                                {Number.isFinite(totalAportado) ? formatCurrency(totalAportado) : '-'}
                              </p>
                            </div>

                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <Target className="w-4 h-4 text-slate-400" />
                                <p className="text-slate-600 text-sm">
                                 {t('remaining')}
                                </p>
                              </div>
                              <p className="text-slate-900 font-semibold">
                                {Number.isFinite(faltaParaMeta) ? formatCurrency(faltaParaMeta) : '-'}
                              </p>
                            </div>

                            <div>
                              <div className="flex items-center gap-2 mb-2">
                                <TrendingUp className="w-4 h-4 text-slate-400" />
                                <p className="text-slate-600 text-sm">
                                  {t('percentageReached')}
                                </p>
                              </div>
                              <p className="text-slate-900 font-semibold">
                                {progresso.toFixed(1)}%
                              </p>
                            </div>
                          </div>

                          {/* ? Plano de Ação só para financeiro */}
                          <Card className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
                            <h4 className="text-slate-900 font-medium mb-3 flex items-center gap-2">
                              <CheckCircle2 className="w-4 h-4" />
                           {t('actionPlan')}
                            </h4>
                            <div className="space-y-2 text-sm">
                              <p className="text-slate-700">
                                • {t('contribute')}{' '}
                                {obj.aportemensal != null ? formatCurrency(obj.aportemensal) : '-'}{' '}
                                {t('monthly')}
                              </p>
                              <p className="text-slate-700">
                                • {t('missing')}{' '}
                                {Number.isFinite(faltaParaMeta) ? formatCurrency(faltaParaMeta) : '-'}{' '}
                                {t('toReachGoal')}
                              </p>
                              <p className="text-slate-700">
                                • {t('expectedCompletion')}{' '}
                                {getDataConclusao(obj.tempoinvestido)}
                              </p>
                              <p className="text-slate-700">
                                • {t('estimatedMonthlyReturn')}{' '}
                                {formatCurrency(obj.retornomensal)}
                              </p>
                            </div>
                          </Card>

                          {/* ? Aportes */}
                          <div className="pt-4 border-t space-y-4">
                            <div className="flex items-center justify-between">
                              <h4 className="text-slate-900 font-medium flex items-center gap-2">
                                <DollarSign className="w-4 h-4" />
                               {t('contributions')}
                              </h4>
                              <Badge variant="outline">
                                {aportes.length} {t('records')}
                              </Badge>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                              <div className="space-y-2">
                                <Label>{t('amount')}</Label>
                                <Input
                                  type="text"
                                  inputMode="numeric"
                                  placeholder={t('currencyPlaceholder')}
                                  value={aporteForm.valor}
                                  onChange={(e) => {
                                    const numeric = e.target.value.replace(/\D/g, '');
                                    const formatted = formatCurrency(Number(numeric) / 100);
                                    setContributionForm(obj._id, { valor: formatted });
                                  }}
                                />
                              </div>

                              <div className="space-y-2">
                                <Label>{t('date')}</Label>
                                <Input
                                  type="date"
                                  value={aporteForm.data}
                                  onChange={(e) => setContributionForm(obj._id, { data: e.target.value })}
                                />
                              </div>

                              <div className="space-y-2 md:col-span-2">
                               <Label>{t('description')}</Label>
                                <Input
                                  type="text"
                                 placeholder={t('contributionDescriptionExample')}
                                  value={aporteForm.descricao}
                                  onChange={(e) => setContributionForm(obj._id, { descricao: e.target.value })}
                                />
                              </div>
                            </div>

                            <div className="flex justify-end gap-2">
                              {editingContributionBySimulation[obj._id] && (
                                <Button
                                  variant="outline"
                                  onClick={() => handleCancelEditContribution(obj._id)}
                                >
                                 {t('cancel')}
                                </Button>
                              )}
                              <Button
                                onClick={() => handleCreateContribution(obj._id)}
                                disabled={isSavingAporte}
                                className="bg-blue-600 hover:bg-blue-700"
                              >
                                {isSavingAporte
                                  ? t('saving')
                                  : editingContributionBySimulation[obj._id]
                                    ? t('updateContribution')
                                    : t('addContribution')}
                              </Button>
                            </div>

                            <div className="space-y-2">
                              {isLoadingAportes ? (
                                <p className="text-sm text-slate-500">
                                  {t('loadingContributions')}
                                </p>
                              ) : aportes.length === 0 ? (
                                <p className="text-sm text-slate-500">
                                  {t('noContributionsYet')}
                                </p>
                              ) : (
                                aportes.slice(0, expandedContributions[obj._id] ? aportes.length : 3).map((item, index) => {
                                  const contributionId = item._id || item.id || `${obj._id}-${index}`;
                                  const valorAporte = item.valor ?? item.valorAportado ?? item.amount ?? 0;
                                  const valorAporteNumber = typeof valorAporte === 'string'
                                    ? parseCurrencyToNumber(valorAporte)
                                    : Number(valorAporte) || 0;
                                  const dataAporte = item.data || item.date;
                                  const descricaoAporte = item.descricao || item.description || '-';

                                  return (
                                    <div
                                      key={contributionId}
                                      className="flex items-start justify-between gap-4 p-3 border rounded-lg bg-slate-50"
                                    >
                                      <div>
                                        <p className="text-slate-900 font-semibold">
                                          {formatCurrency(valorAporteNumber)}
                                        </p>
                                        <p className="text-xs text-slate-500">
                                          {dataAporte
                                            ? new Date(dataAporte).toLocaleDateString(
                                                  language === 'pt'
                                                    ? 'pt-BR'
                                                    : language === 'es'
                                                      ? 'es-ES'
                                                      : 'en-US'
                                                )
                                            : '-'}
                                        </p>
                                        <p className="text-slate-700 text-sm mt-1">
                                          {descricaoAporte}
                                        </p>
                                      </div>

                                      <div className="flex gap-1">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleEditContribution(item, obj._id)}
                                        >
                                          <Pencil className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => handleDeleteContribution(contributionId, obj._id)}
                                          disabled={deletingContributionById[contributionId]}
                                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })
                              )}
                              {aportes.length > 3 && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="w-full text-slate-600"
                                  onClick={() => setExpandedContributions(prev => ({
                                    ...prev,
                                    [obj._id]: !prev[obj._id]
                                  }))}
                                >
                                  {expandedContributions[obj._id]
                                      ? t('showLess')
                                      : t('showAll', { count: aportes.length })}
                                </Button>
                              )}
                            </div>
                          </div>
                        </>
                      )}                   
                    </div>
                  </Card>
                );
              })
            )}
          </div>
    </AppLayout>
  );
}

export default Simulation;

















